import { ImagePickerAsset } from "expo-image-picker";
import { Plus, X } from "lucide-react-native";
import { FC, useCallback, useState } from "react";

import {
  Button,
  ButtonText,
  HStack,
  Input,
  InputField,
  Media,
  MediaGallery,
  MediaPicker,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { DraftCell, VariableDef } from "@/types/resources";

/** Shared height (px) for a cell's text inputs and image pickers, so the
    square media picker lines up with the input field height. Matches the
    `Input` `lg` size (`h-11` = 44px). */
const CELL_FIELD_HEIGHT = 44;

interface CellsEditorProps {
  /** Overrides the "Add cell" button label (use-case wording, e.g. "Add ad"). */
  addLabel?: string;
  cells: DraftCell[];
  disabled?: boolean;
  /** `all` allows video (e.g. ad creative); defaults to images only. */
  mediaType?: "all" | "images";
  maxCells?: number;
  minCells?: number;
  onChange: (cells: DraftCell[]) => void;
  /** Overrides the "Cells" section title (use-case wording, e.g. "Ads"). */
  title?: string;
  variables: VariableDef[];
}

const emptyCell = (variables: VariableDef[]): DraftCell =>
  variables.reduce<DraftCell>((cell, variable) => {
    cell[variable.name] = null;
    return cell;
  }, {});

/** Renderable source for an image variable, or null when empty. A stored key
    resolves to its presigned `<name>_url` sibling; a freshly-picked asset (not
    yet uploaded) renders straight from its local URI. */
const imageSource = (
  cell: DraftCell,
  name: string
): string | ImagePickerAsset | null => {
  const value = cell[name];
  if (!value) return null;
  if (typeof value !== "string") return value;
  const url = cell[`${name}_url`];
  return typeof url === "string" ? url : value;
};

const toUri = (source: string | ImagePickerAsset): string =>
  typeof source === "string" ? source : source.uri;

/**
 * The cells list — one row per rotation unit, one input per declared variable
 * (a text field for `text` variables, an inline media picker for `image`
 * variables). Add/remove rows are bounded by `minCells`/`maxCells`. Purely
 * presentational + local: the caller owns the `cells` array.
 */
const CellsEditor: FC<CellsEditorProps> = ({
  addLabel,
  cells,
  disabled = false,
  mediaType = "images",
  maxCells,
  minCells = 0,
  onChange,
  title,
  variables,
}) => {
  const { i18n } = useLocalization();

  /* A single gallery, opened with the clicked cell's image URLs so the user
     can swipe across every image variable in that row. */
  const [gallery, setGallery] = useState<{
    index: number;
    urls: string[];
  } | null>(null);

  const setValue = useCallback(
    (rowIndex: number, name: string, value: string | ImagePickerAsset | null) =>
      onChange(
        cells.map((cell, i) =>
          i === rowIndex ? { ...cell, [name]: value } : cell
        )
      ),
    [cells, onChange]
  );

  const addCell = useCallback(() => {
    if (maxCells !== undefined && cells.length >= maxCells) return;
    onChange([...cells, emptyCell(variables)]);
  }, [cells, maxCells, onChange, variables]);

  const removeCell = useCallback(
    (index: number) => {
      if (cells.length <= minCells) return;
      onChange(cells.filter((_, i) => i !== index));
    },
    [cells, minCells, onChange]
  );

  /* A cell field is keyed by the variable name — an unnamed variable has no
     key yet, so skip its column until it's named (otherwise every unnamed
     variable would share the empty-string key and mirror each other). */
  const namedVariables = variables.filter((variable) => variable.name.trim());

  return (
    <VStack className="gap-3">
      <Text className="text-sm font-semibold text-typography-800">
        {title ?? i18n.t("studies.cells.cells")}
      </Text>

      {namedVariables.length === 0 ? (
        <Text className="text-xs text-typography-400 pl-4">
          {i18n.t("studies.cells.noVariables")}
        </Text>
      ) : (
        <VStack className="gap-2 pl-4">
          {cells.map((cell, rowIndex) => {
            const canRemove = cells.length > minCells;
            /* Every filled image in this row, in variable order — the gallery
               swipes across exactly these. */
            const rowImages = namedVariables
              .filter((v) => v.type === "image")
              .map((v) => imageSource(cell, v.name))
              .filter(
                (source): source is string | ImagePickerAsset => source !== null
              );
            return (
              <HStack key={rowIndex} className="items-center gap-2.5">
                {namedVariables.map((variable, varIndex) => {
                  if (variable.type === "image") {
                    const source = imageSource(cell, variable.name);
                    /* One image per variable, admin or not: a filled variable
                       shows only its thumbnail (opening the shared row gallery
                       on press), an empty one shows the upload picker. */
                    return source ? (
                      <Media
                        key={varIndex}
                        alt={variable.name}
                        height={CELL_FIELD_HEIGHT}
                        onDelete={
                          disabled
                            ? undefined
                            : () => setValue(rowIndex, variable.name, null)
                        }
                        onPress={() =>
                          setGallery({
                            index: rowImages.indexOf(source),
                            urls: rowImages.map(toUri),
                          })
                        }
                        resizeMode="cover"
                        source={source}
                        width={CELL_FIELD_HEIGHT}
                      />
                    ) : (
                      <MediaPicker
                        key={varIndex}
                        isDisabled={disabled}
                        maxItems={1}
                        media={[]}
                        mediaType={mediaType}
                        onMediaChange={(items) => {
                          /* Keep the picked asset as-is so its mime/extension
                             survive to upload time (videos would otherwise be
                             misfiled). Already-stored keys stay strings. */
                          const item = items[0] as
                            ImagePickerAsset | string | undefined;
                          setValue(rowIndex, variable.name, item ?? null);
                        }}
                      >
                        <MediaPicker.Trigger
                          compact
                          height={CELL_FIELD_HEIGHT}
                          iconSize={14}
                          width={CELL_FIELD_HEIGHT}
                        />
                      </MediaPicker>
                    );
                  }
                  const textValue = cell[variable.name];
                  return (
                    <Input
                      key={varIndex}
                      className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                      style={{ height: CELL_FIELD_HEIGHT }}
                    >
                      <InputField
                        onChangeText={(text) =>
                          setValue(rowIndex, variable.name, text)
                        }
                        placeholder={variable.name}
                        value={typeof textValue === "string" ? textValue : ""}
                      />
                    </Input>
                  );
                })}
                {canRemove && (
                  <Pressable
                    accessibilityLabel={i18n.t("studies.cells.removeCell")}
                    className="p-1.5 rounded-md hover:bg-error-50"
                    onPress={() => removeCell(rowIndex)}
                  >
                    <X color="rgb(var(--color-typography-500))" size={16} />
                  </Pressable>
                )}
              </HStack>
            );
          })}

          {(maxCells === undefined || cells.length < maxCells) && (
            <Button
              className="self-start rounded-lg border-dashed"
              onPress={addCell}
              size="sm"
              variant="outline"
            >
              <Plus color="rgb(var(--color-typography-600))" size={14} />
              <ButtonText className="text-sm text-typography-600 ml-1">
                {addLabel ?? i18n.t("studies.cells.addCell")}
              </ButtonText>
            </Button>
          )}
        </VStack>
      )}

      {gallery && (
        <MediaGallery
          initialIndex={gallery.index}
          media={gallery.urls}
          onClose={() => setGallery(null)}
          visible
        />
      )}
    </VStack>
  );
};

export default CellsEditor;
